'use client';

import { CaretDownIcon } from '@phosphor-icons/react';
import Link from 'next/link';

import HeaderMobileDropdownList from './HeaderMobileDropdownList';
import HeaderMobileMegaSection from './HeaderMobileMegaSection';
import { Badge } from '../../lib/header-constants';
import { cn } from '@/shared/utils/cn';
import { useNavbarContext } from './context';
import type { NavItem } from '../../types/navigation';

interface HeaderMobileNavItemProps {
  expandedGroupIds: string[];
  isExpanded: boolean;
  item: NavItem;
  onClose: () => void;
  onToggleGroup: (groupId: string) => void;
  onToggleItem: () => void;
}

const MOBILE_NAV_ITEM_CLASS = cn(
  'flex min-h-12 w-full items-center justify-between rounded-adipa-md px-3 py-2 text-left text-sm',
  'transition-colors duration-adipa-fast ease-out motion-reduce:transition-none',
);

const getMobileNavItemStateClass = (isActive?: boolean) =>
  isActive
    ? 'font-semibold text-adipa-primary hover:bg-adipa-surface-lavender'
    : 'font-medium text-adipa-text-primary hover:bg-adipa-surface-lavender hover:font-semibold hover:text-adipa-primary';

const HeaderMobileNavItem = ({
  expandedGroupIds,
  isExpanded,
  item,
  onClose,
  onToggleGroup,
  onToggleItem,
}: HeaderMobileNavItemProps) => {
  const { onNavClick } = useNavbarContext();
  const hasDropdown = Boolean(item.dropdown?.length || item.megaMenu);

  if (!hasDropdown) {
    return (
      <li>
        <Link
          className={cn(MOBILE_NAV_ITEM_CLASS, getMobileNavItemStateClass(item.active))}
          href={item.href}
          prefetch={false}
          onClick={() => {
            onNavClick?.({ href: item.href, label: item.label, url: item.href });
            onClose();
          }}
        >
          <span>{item.label}</span>
          {item.badge ? <Badge color={item.badgeColor!} label={item.badge} /> : null}
        </Link>
      </li>
    );
  }

  return (
    <li>
      <button
        aria-expanded={isExpanded}
        className={cn(MOBILE_NAV_ITEM_CLASS, getMobileNavItemStateClass(item.active))}
        type="button"
        onClick={onToggleItem}
      >
        <span className="min-w-0 flex-1">{item.label}</span>

        <span className="ml-2 flex shrink-0 items-center gap-2">
          {item.badge ? <Badge color={item.badgeColor!} label={item.badge} /> : null}
          <CaretDownIcon
            className={cn(
              'duration-adipa-fast transition-transform ease-out motion-reduce:transition-none',
              isExpanded && 'rotate-180',
            )}
            size={14}
            weight="bold"
          />
        </span>
      </button>

      <div
        className={cn(
          'mobile-accordion-panel',
          isExpanded ? 'mobile-accordion-panel-open' : 'mobile-accordion-panel-closed',
        )}
      >
        <div className="min-h-0">
          {item.megaMenu ? (
            <HeaderMobileMegaSection
              expandedGroupIds={expandedGroupIds}
              item={item}
              onClose={onClose}
              onToggleGroup={onToggleGroup}
            />
          ) : (
            <HeaderMobileDropdownList items={item.dropdown || []} onClose={onClose} />
          )}
        </div>
      </div>
    </li>
  );
};

export default HeaderMobileNavItem;
